import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// MUI Components
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Typography,
  styled,
  Modal,
  Stack,
  Button
} from '@mui/material';

// MUI Icons
import {
  Home,
  Add,
  ExpandLess,
  ExpandMore,
  PushPin,
  PushPinOutlined,
  ViewList as CatalogIcon,
  LocalShipping as LocalShippingIcon,
  Assessment as AssessmentIcon,
  FileOpenOutlined as FileOpenOutlinedIcon,
  BusinessCenter as BusinessCenterIcon,
  Category as CategoryIcon,
  QrCode as QrCodeIcon,
  Label as LabelIcon,
  MonetizationOn as MonetizationOnIcon,
  Speed as SpeedIcon,
  DeleteOutline as DeleteOutlineIcon,
  ReceiptLong as ReceiptLongIcon,
  TrendingUp as TrendingUpIcon,
  GamesOutlined as GamesIcon,
} from '@mui/icons-material';

// Contexts
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';

import ProductMetadataGame from '../MissingProductsGame/ProductMetadataGame';
import { MarterIcon } from '../MarterIcon';

const DRAWER_WIDTH = 240;
const MINI_DRAWER_WIDTH = 65;
const NAVBAR_HEIGHT = '65px';

const StyledDrawer = styled(Drawer)(({ theme, open, customtheme }) => ({
  width: open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    top: NAVBAR_HEIGHT,
    width: open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    border: 'none',
    borderRight: `1px solid ${customtheme.border.medium}`,
    backgroundColor: customtheme.background.tertiary,
  },
  '& .MuiListItemText-root, .MuiListItemButton-root': {
    fontFamily: 'Roboto Slab, Arial, sans-serif !important',
  },
}));

const SubMenuContainer = styled(Box)(({ customtheme }) => ({
  maxHeight: '-webkit-fill-available',
  overflowY: 'auto',
  overflowX: "hidden",
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: customtheme.background.tertiary,
  },
  '&::-webkit-scrollbar-thumb': {
    background: customtheme.scrollbar,
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: customtheme.scrollbarHover,
  },
}));

const Sidebar = (props) => {
  const { theme } = useTheme();
  const { user, password } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState('');
  const [openQuestionModal, setOpenQuestionModal] = useState(false);
  const [pageSelected, setPageSelected] = useState("");
  const [isGameOpen, setIsGameOpen] = useState(false);
  const navigate = useNavigate();

  const testers = ["omrileizerovitch7@gmail.com", "friedman.ido@gmail.com"];

  const glowStyle = {
    animation: 'pulseGlow 1s ease-in-out infinite alternate',
    '@keyframes pulseGlow': {
      '0%': {
        filter: `
        drop-shadow(0 0 4px ${theme.accent.warning}CC)
        drop-shadow(0 0 8px ${theme.accent.warning}99)
        drop-shadow(0 0 16px ${theme.accent.warning}66)
        drop-shadow(0 0 32px ${theme.accent.warning}33)
      `
      },
      '100%': {
        filter: `
        drop-shadow(0 0 6px ${theme.accent.warning})
        drop-shadow(0 0 12px ${theme.accent.warning}CC)
        drop-shadow(0 0 24px ${theme.accent.warning}99)
        drop-shadow(0 0 48px ${theme.accent.warning}66)
      `
      }
    }
  };

  const menuItems = [
    { text: 'Home', icon: <Home />, path: '/', handleClick: () => navigate('/'), },
    { text: 'Add & Send Items', icon: <Add />, path: '/add-item', handleClick: () => { if (testers.includes(user)) { setPageSelected('/add-item'); setOpenQuestionModal(true); } else { navigate('/add-item') } } },
    { text: 'Catalog', icon: <CatalogIcon />, path: '/catalog', handleClick: () => navigate('/catalog'), },
    { text: 'Shipments', icon: <LocalShippingIcon />, path: '/shipments', handleClick: () => navigate('/shipments'), },
    { text: 'Add Marter Buy List', icon: <MarterIcon />, path: '/marter-buy-list', handleClick: () => { if (testers.includes(user)) { setPageSelected('/marter-buy-list'); setOpenQuestionModal(true); } else { navigate('/marter-buy-list') } } },
    { text: 'Upload from CSV', icon: <FileOpenOutlinedIcon />, path: '/csv-upload', handleClick: () => { if (testers.includes(user)) { setPageSelected('/csv-upload'); setOpenQuestionModal(true); } else { navigate('/csv-upload') } } },
    // {
    //   text: 'Reports',
    //   icon: <AssessmentIcon sx={glowStyle} />,
    //   path: '/reports',
    //   subItems: [
    //     { text: 'Supplier Profitability', icon: <BusinessCenterIcon sx={{ fontSize: 20 }} />, path: '/reports/supplier', handleClick: () => navigate('/reports/supplier') },
    //     { text: 'Category Profitability', icon: <CategoryIcon sx={{ fontSize: 20 }} />, path: '/reports/category', handleClick: () => navigate('/reports/category') },
    //     { text: 'SKU Profitability', icon: <QrCodeIcon sx={{ fontSize: 20 }} />, path: '/reports/sku', handleClick: () => navigate('/reports/sku') },
    //     { text: 'Item ID Profitability', icon: <LabelIcon sx={{ fontSize: 20 }} />, path: '/reports/item-id', handleClick: () => navigate('/reports/item-id') },
    //     { text: 'Inventory Valuation', icon: <MonetizationOnIcon sx={{ fontSize: 20 }} />, path: '/reports/inventory', handleClick: () => navigate('/reports/inventory') },
    //     { text: 'Sales Tax Report', icon: <ReceiptLongIcon sx={{ fontSize: 20 }} />, path: '/reports/sales-tax', handleClick: () => navigate('/reports/sales-tax') },
    //     { text: 'Profit & Loss', icon: <TrendingUpIcon sx={{ fontSize: 20 }} />, path: '/reports/profit-loss', handleClick: () => navigate('/reports/profit-loss') },
    //   ]
    // },
    { text: 'Add Missing Info', icon: <GamesIcon sx={glowStyle} />, path: '#', handleClick: () => { setIsGameOpen(true); setOpen(false) } },
  ]

  const isSelected = (itemPath) => {
    if (itemPath === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(itemPath);
  };

  const handleMouseEnter = () => {
    if (!isPinned && !isGameOpen) {
      setOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isPinned && !isGameOpen) {
      setOpen(false);
    }
  };

  const handlePinClick = () => {
    if (!isPinned) {
      props.setWidth(DRAWER_WIDTH);
    } else {
      props.setWidth(MINI_DRAWER_WIDTH);
    }
    setIsPinned(!isPinned);
    setOpen(!isPinned);
  };

  const handleClick = (item) => {
    if (item.subItems) {
      setOpenSubMenu(openSubMenu === item.text ? '' : item.text);
    } else if (item.handleClick) {
      item.handleClick();
    }
  };

  const handleConfirm = () => {
    setOpen(false);
    setOpenQuestionModal(false);
    navigate(`${pageSelected}s-bulk`);
  };

  const handleCancel = () => {
    setOpen(false);
    setOpenQuestionModal(false);
    navigate(pageSelected);
  };

  const styles = {
    modal: {
      bgcolor: theme.background.primary,
      border: `4px solid ${theme.color.primary}`,
      p: 3,
      textAlign: 'center',
      maxWidth: 400,
      borderRadius: 2,
      boxShadow: 3,
      mx: 'auto',
      mt: 10
    },
    text: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      border: `1px solid ${theme.border.light}`
    }
  };

  if (props.isMobile) {
    return null;
  }

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ display: 'flex', position: 'fixed', fontFamily: theme.fontFamily, zIndex: open ? 9999 : 1 }}
    >
      <StyledDrawer
        variant="permanent"
        open={open}
        customtheme={theme}
      >
        <List>
          {menuItems.map((item) => {
            const selected = isSelected(item.path);
            return (
              <Box key={item.text}>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={() => handleClick(item)}
                    selected={selected}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: theme.background.secondary,
                        },
                      },
                      '&:hover': {
                        backgroundColor: theme.background.secondary,
                      },
                      minHeight: 36,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: selected ? theme.color.primary : theme.text.tertiary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography sx={{ fontFamily: theme.fontFamily }}>
                          {item.text}
                        </Typography>
                      }
                      sx={{
                        opacity: open ? 1 : 0,
                        color: selected ? theme.color.primary : theme.text.primary,
                      }}
                    />
                    {item.subItems && open && (
                      <Box sx={{ color: theme.text.tertiary }}>
                        {openSubMenu === item.text ? <ExpandLess /> : <ExpandMore />}
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>

                {item.subItems && (
                  <Collapse in={openSubMenu === item.text && open} timeout="auto" unmountOnExit>
                    <SubMenuContainer customtheme={theme}>
                      <List component="div" disablePadding>
                        {item.subItems.map((subItem) => {
                          const subSelected = isSelected(subItem.path);
                          return (
                            <ListItemButton
                              key={subItem.text}
                              onClick={subItem.handleClick}
                              selected={subSelected}
                              sx={{
                                height: 40,
                                '&.Mui-selected': {
                                  backgroundColor: 'transparent',
                                  '&:hover': {
                                    backgroundColor: theme.background.secondary,
                                  },
                                },
                                '&:hover': {
                                  backgroundColor: theme.background.secondary,
                                },
                              }}
                            >
                              {subItem.icon && (
                                <ListItemIcon
                                  sx={{
                                    minWidth: 0,
                                    mr: 2,
                                    ml: 1,
                                    justifyContent: 'center',
                                    color: subSelected ? theme.color.primary : theme.text.tertiary,
                                  }}
                                >
                                  {subItem.icon}
                                </ListItemIcon>
                              )}
                              <ListItemText
                                primary={
                                  <Typography sx={{ fontFamily: theme.fontFamily, fontSize: 14 }}>
                                    {subItem.text}
                                  </Typography>
                                }
                                sx={{
                                  color: subSelected ? theme.color.primary : theme.text.primary,
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </SubMenuContainer>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>

        <Divider sx={{ borderColor: theme.border.medium }} />

        <Box sx={{
          position: 'fixed',
          bottom: 0,
          width: open ? DRAWER_WIDTH : MINI_DRAWER_WIDTH,
          backgroundColor: theme.background.tertiary,
        }}>
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              onClick={handlePinClick}
              customtheme={theme}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: theme.background.secondary,
                  },
                },
                '&:hover': {
                  backgroundColor: theme.background.secondary,
                },
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                  color: theme.text.tertiary,
                }}
              >
                {isPinned ? <PushPin /> : <PushPinOutlined />}
              </ListItemIcon>
              <ListItemText
                primary={isPinned ? <Typography sx={{ fontFamily: theme.fontFamily }}>Unpin</Typography> : <Typography sx={{ fontFamily: theme.fontFamily }}>Pin</Typography>}
                sx={{
                  opacity: open ? 1 : 0,
                  color: theme.text.primary,
                }}
              />
            </ListItemButton>
          </ListItem>
        </Box>
      </StyledDrawer>

      <Modal open={openQuestionModal} onClose={() => setOpenQuestionModal(false)}>
        <Box sx={styles.modal}>
          <Typography variant="h6" gutterBottom sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>
            Do You Have More Than 10 Items?
          </Typography>
          <Typography variant="body1" mb={2} sx={{ fontFamily: theme.fontFamily, color: theme.text.tertiary }}>
            If you have more than 10 items to upload, we recommend using bulk upload.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="contained" sx={{ ...styles.button, backgroundColor: theme.accent.success, }} onClick={handleConfirm} >
              Yes, I Have More
            </Button>
            <Button variant="contained" sx={{ ...styles.button, backgroundColor: theme.accent.error, }} onClick={handleCancel}>
              No, Add Manually
            </Button>
          </Stack>
        </Box>
      </Modal>
      <Modal
        open={isGameOpen}
        onClose={() => setIsGameOpen(false)}
        sx={{ backdropFilter: 'blur(3px)' }}
      >
        <Box sx={{
          outline: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 2
        }}>
          <ProductMetadataGame isOpen={isGameOpen} onClose={() => setIsGameOpen(false)} />
        </Box>
      </Modal>
    </Box>
  );
};

export default Sidebar;